/**
 * TanStack Query provider for the SmartSafe Portal SPA.
 *
 * Configures QueryClient with:
 * - 5s staleTime for real-time data (alerts, events, device status)
 * - 60s staleTime for config data (user settings, tenant config)
 * - Retry with exponential backoff (3 attempts)
 *
 * Requirements: 28.1, 28.2, 28.6
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { ReactNode } from 'react';

/** Stale time for real-time data (alerts, events, device status) */
export const REALTIME_STALE_TIME = 5_000;

/** Stale time for configuration data (user settings, tenant config) */
export const CONFIG_STALE_TIME = 60_000;

/**
 * Creates the QueryClient with default options.
 * Exported for use in tests and the WS query updater.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: REALTIME_STALE_TIME,
      gcTime: 5 * 60_000,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30_000),
      refetchOnWindowFocus: true,
    },
    mutations: {
      retry: 1,
    },
  },
});

interface QueryProviderProps {
  children: ReactNode;
  client?: QueryClient;
}

/**
 * Wraps the application with TanStack Query context.
 */
export function QueryProvider({ children, client = queryClient }: QueryProviderProps) {
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}
